import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { DEFAULT_TEXTS } from "@/lib/texts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
    { key: "hero_overline", label: "Home in alto — Occhiello" },
    { key: "hero_title_1", label: "Home in alto — Titolo riga 1" },
    { key: "hero_title_2", label: "Home in alto — Titolo riga 2" },
    { key: "hero_title_3", label: "Home in alto — Titolo riga 3 (in evidenza)" },
    { key: "hero_paragraph", label: "Home in alto — Paragrafo", multiline: true },
    { key: "hero_cta_portfolio", label: "Home in alto — Bottone scuro (porta al portfolio)" },
    { key: "hero_cta_contact", label: "Home in alto — Bottone chiaro (porta ai contatti)" },
    { key: "marquee_items", label: "Striscia scorrevole — Voci (una per riga)", multiline: true },
    { key: "about_eyebrow", label: "Chi Siamo — Etichetta piccola" },
    { key: "about_heading", label: "Chi Siamo — Titolo" },
    { key: "about_p1", label: "Chi Siamo — Paragrafo 1", multiline: true },
    { key: "about_p2", label: "Chi Siamo — Paragrafo 2", multiline: true },
    { key: "about_caption", label: "Chi Siamo — Didascalia sulla foto" },
    { key: "about_quote", label: "Chi Siamo — Frase in corsivo sulla foto" },
    { key: "about_badge", label: "Chi Siamo — Bollino rotondo (una riga per riga)", multiline: true },
    { key: "services_heading", label: "Servizi — Titolo" },
    { key: "services_subtitle", label: "Servizi — Sottotitolo", multiline: true },
    { key: "portfolio_heading", label: "Realizzazioni — Titolo" },
    { key: "testimonials_heading", label: "Recensioni — Titolo" },
    { key: "whyus_heading", label: "Perché Noi — Titolo" },
    { key: "whyus_paragraph", label: "Perché Noi — Paragrafo", multiline: true },
    { key: "contact_heading", label: "Contatti — Titolo" },
    { key: "contact_paragraph", label: "Contatti — Paragrafo", multiline: true },
    { key: "servizi_page_title", label: "Pagina Servizi — Titolo" },
    { key: "servizi_page_intro", label: "Pagina Servizi — Introduzione", multiline: true },
];

const inputCls =
    "w-full border border-stone-900/15 bg-white px-4 py-3 text-sm text-stone-900 outline-none transition-colors focus:border-[#E75C15]";

export const TextsForm = ({ settings }) => {
    const [texts, setTexts] = useState(DEFAULT_TEXTS);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (settings?.texts) setTexts({ ...DEFAULT_TEXTS, ...settings.texts });
    }, [settings]);

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/settings/texts`, { value: texts }, { withCredentials: true });
            toast.success("Testi aggiornati: sono già visibili sul sito.");
        } catch {
            toast.error("Salvataggio non riuscito.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-3xl" data-testid="texts-form">
            <h2 className="font-display text-2xl font-light text-stone-900">Testi della home</h2>
            <p className="mt-2 text-sm leading-relaxed text-stone-600">
                Modifica i testi principali delle sezioni. Dopo il salvataggio sono subito
                visibili sul sito.
            </p>
            <div className="mt-8 space-y-6">
                {FIELDS.map((f) => (
                    <div key={f.key}>
                        <label
                            htmlFor={`text-${f.key}`}
                            className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500"
                        >
                            {f.label}
                        </label>
                        {f.multiline ? (
                            <textarea
                                id={`text-${f.key}`}
                                data-testid={`text-input-${f.key}`}
                                rows={f.key === "marquee_items" ? 6 : 3}
                                className={`${inputCls} resize-y`}
                                value={texts[f.key] || ""}
                                onChange={(e) => setTexts((t) => ({ ...t, [f.key]: e.target.value }))}
                            />
                        ) : (
                            <input
                                id={`text-${f.key}`}
                                data-testid={`text-input-${f.key}`}
                                className={inputCls}
                                value={texts[f.key] || ""}
                                onChange={(e) => setTexts((t) => ({ ...t, [f.key]: e.target.value }))}
                            />
                        )}
                    </div>
                ))}
            </div>
            <button
                data-testid="texts-save-button"
                onClick={save}
                disabled={saving}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1C1917] px-8 py-4 text-sm font-medium text-stone-100 transition-colors duration-300 hover:bg-[#B8430D] disabled:opacity-60"
            >
                {saving ? (
                    <>
                        Salvo… <Loader2 className="h-4 w-4 animate-spin" />
                    </>
                ) : (
                    "Salva i Testi"
                )}
            </button>
        </div>
    );
};
